/**
 * services/CriteriaService.ts
 * Service for managing playbook criteria items
 *
 * Criteria items are reusable entry conditions that can be assigned to playbooks.
 * They can optionally be organized under MarketConditionCategories.
 *
 * Extends BaseService to provide CRUD operations with status-based filtering
 * and category-based queries.
 */

import { BaseService } from './BaseService';
import type { PlaybookCriteriaItem } from '@/types/schema/PlaybookCriteriaItem';
import type { EntityStatus } from '@/types/shared/EntityStatus';
import { isEntityActive } from '@/types/shared/EntityStatus';

/**
 * Service for managing playbook criteria items
 *
 * Provides CRUD operations plus status-based and category-based filtering.
 * Inherits getAll(), get(), update(), softDelete() from BaseService.
 */
export class CriteriaService extends BaseService<PlaybookCriteriaItem> {
  constructor() {
    super('playbook_criteria_items');
  }

  /**
   * Add a new criteria item with default status
   *
   * Defaults to status: 'active' if not provided.
   *
   * @param data - Partial criteria item data
   * @returns Promise resolving to the created criteria item
   */
  override async add(data: Partial<PlaybookCriteriaItem>): Promise<PlaybookCriteriaItem> {
    return super.add({ status: 'active', ...data });
  }

  /**
   * Get criteria items by status
   *
   * Filters criteria by the specified EntityStatus.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @param status - The status to filter by ('active' | 'inactive' | 'archived')
   * @returns Promise resolving to array of criteria with matching status
   */
  async getByStatus(status: EntityStatus): Promise<PlaybookCriteriaItem[]> {
    const allCriteria = await this.getAll();
    return allCriteria.filter(criteria => criteria.status === status);
  }

  /**
   * Get all active criteria items
   *
   * Convenience method for getting criteria where status is 'active'.
   * Uses getAll() to ensure tombstoned records are excluded.
   *
   * @returns Promise resolving to array of active criteria
   */
  async getActiveCriteria(): Promise<PlaybookCriteriaItem[]> {
    const allCriteria = await this.getAll();
    return allCriteria.filter(criteria => isEntityActive(criteria.status));
  }

  /**
   * Get criteria items by category
   *
   * Filters criteria by marketConditionCategoryId.
   * If categoryId is undefined, returns uncategorized items (where category is undefined).
   *
   * @param categoryId - The category ID to filter by, or undefined for uncategorized items
   * @returns Promise resolving to array of criteria in the specified category
   */
  async getByCategoryId(categoryId: string | undefined): Promise<PlaybookCriteriaItem[]> {
    const allCriteria = await this.getAll();
    return allCriteria.filter(criteria => criteria.marketConditionCategoryId === categoryId);
  }
}

// Export singleton instance
export const criteriaService = new CriteriaService();
